import ApplicationInstance from "@ember/application/instance";
import { registerDestructor } from "@ember/destroyable";
import Modifier, {
  type ArgsFor,
  type NamedArgs,
  type PositionalArgs
} from "ember-modifier";
import "../vendor/transition.min.js";

export interface Signature {
  Element: HTMLElement;
  Args: {
    Named: Record<string, unknown>;
    Positional: [settings?: Record<string, unknown>];
  };
}

type SemanticModule = ((...args: unknown[]) => unknown) & {
  settings: Record<string, unknown>;
};

/**
 * The FUI vendor scripts add one jQuery method per module (`$(element).dropdown(…)`).
 */
type JQueryWithSemanticModules = JQuery & Record<string, SemanticModule>;

export default abstract class FuiModuleModifier<
  S extends Signature = Signature
> extends Modifier<S> {
  constructor(owner: ApplicationInstance, args: ArgsFor<S>) {
    super(owner, args);
    registerDestructor(this, () => {
      /*
      Cleanup the semantic module when the modifier's element is removed from the DOM

      In some cases [detected in production](https://semeia-eu.sentry.io/issues/31708462/?environment=production&project=4508143674458192&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&statsPeriod=7d&stream_index=0),
      `this.semanticModule` might be `undefined` and I don't know why/how…

      In these cases, we do noting
      */
      if (this.semanticModule) {
        this.cleanupBeforeDestroy();
        this.semanticModule("destroy");
      }
    });
  }

  /**
   * Hook for subclasses to run module-specific teardown before `destroy`.
   * Base modules need nothing here.
   */
  protected cleanupBeforeDestroy(): void {}

  jqueryObject?: JQueryWithSemanticModules;
  abstract semanticModuleName: string;

  get settings(): Record<string, unknown> {
    return {};
  }

  get semanticModule(): (...args: unknown[]) => unknown {
    return this.jqueryObject![this.semanticModuleName]!.bind(this.jqueryObject);
  }

  settingExistsOnModule(setting: string): boolean {
    return Object.keys(
      this.jqueryObject![this.semanticModuleName]!.settings
    ).includes(setting);
  }

  override modify(
    element: S["Element"],
    positional: PositionalArgs<S>,
    named: NamedArgs<S>
  ): void {
    if (this.jqueryObject) return;

    this.jqueryObject = $(element) as JQueryWithSemanticModules;

    const settings = this.settings;

    let lastPositionalArg;

    if (positional.length > 0)
      lastPositionalArg = positional[positional.length - 1];

    const settingArgs =
      typeof lastPositionalArg === "object" ? lastPositionalArg : named;

    Object.entries(settingArgs).forEach(([setting, value]) => {
      if (this.settingExistsOnModule(setting)) settings[setting] = value;
    });

    this.semanticModule(settings);
  }
}
